import { faClose, faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ArrowIcon,
  Flex,
  Image,
  Text,
  TextButton,
  TextLink,
  Tooltip,
} from '@modusign/design-system';
import { useState } from 'react';

import IconShare from '@app/assets/icons/icon-share.svg';
import LoadingSpinner from '@app/components/LoadingSpinner';
import { ResourceRoleSubjectChip } from '@app/features/authorization';
import useGetUserAutoSharingTargets from '@app/features/workspaceManagement/hooks/useGetUserAutoSharingTargets';
import useResponsiveValue from '@app/hooks/useResponsiveValue';
import { useCurrentUserMemberId } from '@app/hooks/workspace';

export default function DocumentAutoSharingContent() {
  const isMobileWidth = useResponsiveValue([true, false]);
  const memberId = useCurrentUserMemberId();
  const currentUserAutomaticSharingTargets = useGetUserAutoSharingTargets({
    memberId,
  });

  const [tooltipOpen, setTooltipOpen] = useState(false);

  if (currentUserAutomaticSharingTargets.data?.length === 0) {
    return null;
  }

  return (
    <Flex flexDirection="column" mt={6}>
      <Flex alignItems="center">
        <Flex alignItems="center" height="18px" justifyContent="center" mr="2px" width="18px">
          <Image alt="문서 자동 공유" src={IconShare} sx={{ color: '#848484' }} width="14px" />
        </Flex>
        <Text as="p" color="textNeutralLight">
          문서 자동 공유
        </Text>
        {!isMobileWidth && (
          <Tooltip
            content={
              <Flex alignItems="start" flexDirection="column">
                <Text as="p" fontSize="sm">
                  {`설정 > 문서 자동 공유에서 변경할 수 있으며`}{' '}
                  <TextButton
                    sx={{ height: '20px', ml: 1, width: '20px' }}
                    onClick={() => setTooltipOpen(false)}
                  >
                    <FontAwesomeIcon icon={faClose} style={{ color: '#FFFFFF' }} />
                  </TextButton>
                  <br />
                  {`‘멤버 관리’ 권한이 필요합니다.`}
                </Text>
                <TextLink
                  display="block"
                  href="/settings/workspace/automatic-sharing"
                  mt={1}
                  sx={{ '&:hover': { color: '#BABABA' } }}
                  target="_blank"
                >
                  <Text color="textNeutralUltraLight" fontSize="13px">
                    설정 바로가기
                  </Text>{' '}
                  <ArrowIcon direction="right" ml="2px" />
                </TextLink>
              </Flex>
            }
            disabled={!tooltipOpen}
            isOpen={tooltipOpen}
            scale="md"
          >
            <Flex
              alignItems="end"
              height="18px"
              justifyContent="center"
              ml={2}
              sx={{ '&:hover': { cursor: 'pointer' } }}
              width="18px"
              onClick={() => setTooltipOpen((prev) => !prev)}
            >
              <FontAwesomeIcon icon={faQuestionCircle} style={{ color: '#757575' }} />
            </Flex>
          </Tooltip>
        )}
      </Flex>
      {currentUserAutomaticSharingTargets.isFetching && (
        <Flex justifyContent="center" p={6}>
          <LoadingSpinner size={24} />
        </Flex>
      )}
      {currentUserAutomaticSharingTargets.isError && (
        <Flex flexDirection="column" justifyContent="center" p={6} sx={{ gap: 2 }}>
          <Text as="p">멤버·그룹 정보를 불러오지 못했습니다.</Text>
          <TextButton
            onClick={() => {
              currentUserAutomaticSharingTargets.refetch();
            }}
          >
            다시 시도
          </TextButton>
        </Flex>
      )}
      {!currentUserAutomaticSharingTargets.isFetching && (
        <Flex flexWrap="wrap" px={6} py={4} sx={{ gap: 3 }}>
          {currentUserAutomaticSharingTargets.data?.map((t) => (
            <ResourceRoleSubjectChip key={t.subjectId}>
              {t.subjectType === 'MEMBER' ? t.memberName : t.groupName}
            </ResourceRoleSubjectChip>
          ))}
        </Flex>
      )}
    </Flex>
  );
}
